import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useDashboardAggregate } from "../../hooks/useDashboardAggregate";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { AggregateRequest } from "../../types/dashboard/DashboardConfig";
import LoadingIndicator from "../../components/common/LoadingIndicator/LoadingIndicator";
import Chart from "react-apexcharts";
import { useTheme } from "@mui/material/styles";
import { numberWithCommas } from "../../helpers/numberWithCommas";
import { buildChartConfig } from "./buildChartConfig";
import type { AggregationType } from "./widgetBuilderConstants";

interface WidgetPreviewProps {
  title: string;
  chartType: string;
  aggregationType: AggregationType;
  field: string;
  interval?: string;
  size?: number;
  filters?: {
    from?: string;
    to?: string;
    agentName?: string;
    direction?: string;
    interactionType?: string;
    skill?: string;
    group?: string;
    tags?: string[];
  };
}

const DEBOUNCE_MS = 300;

export default function WidgetPreview({
  title,
  chartType,
  aggregationType,
  field,
  interval,
  size,
  filters,
}: Readonly<WidgetPreviewProps>) {
  const { customerId } = useParams();
  const theme = useTheme();

  const request: AggregateRequest | null = useMemo(() => {
    if (aggregationType !== "count" && !field) return null;

    return {
      aggregationType,
      field: aggregationType === "count" ? undefined : field,
      interval: aggregationType === "dateHistogram" ? interval : undefined,
      size: aggregationType === "terms" ? size : undefined,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      from: filters?.from,
      to: filters?.to,
      agentName: filters?.agentName,
      direction: filters?.direction,
      interactionType: filters?.interactionType,
      skill: filters?.skill,
      group: filters?.group,
      tags: filters?.tags,
    };
  }, [aggregationType, field, interval, size, filters]);

  const debouncedRequest = useDebouncedValue(request, DEBOUNCE_MS);
  const { data, isLoading, error } = useDashboardAggregate(
    customerId,
    debouncedRequest,
    "preview"
  );

  const primaryColor = theme.palette.customColors?.wDarkBlue;
  const customColors = useMemo(
    () => (theme.palette.customColors ? Object.values(theme.palette.customColors) : undefined),
    [theme.palette.customColors],
  );

  // ApexCharts can't handle in-place updates when chart type changes OR when
  // the data shape changes significantly (e.g. 12 months → 70 months after
  // removing a date filter).  Build a "chart identity" from chart type + data
  // boundaries so we force a clean unmount/remount whenever either changes.
  const labels = data?.labels;
  const chartIdentity = `${chartType}-${labels?.length ?? 0}-${labels?.[0] ?? ""}-${labels?.[labels.length - 1] ?? ""}`;

  const prevIdentityRef = useRef(chartIdentity);
  const [chartReady, setChartReady] = useState(true);

  // Detect change synchronously during render so React never paints the Chart
  // in a transitional state.  Skip the initial null→value transition.
  if (prevIdentityRef.current !== chartIdentity) {
    const wasEmpty = prevIdentityRef.current.endsWith("-0--");
    prevIdentityRef.current = chartIdentity;
    if (chartReady && !wasEmpty) {
      setChartReady(false);
    }
  }

  useEffect(() => {
    if (!chartReady) {
      const frame = requestAnimationFrame(() => setChartReady(true));
      return () => cancelAnimationFrame(frame);
    }
  }, [chartReady]);

  // Memoize chart config so ApexCharts doesn't redraw on parent re-renders.
  // Title is shown above the chart, so we use a static series name to avoid
  // recalculating (and corrupting the chart) on every title keystroke.
  const chartConfig = useMemo(
    () => (data ? buildChartConfig(data, chartType, "Value", primaryColor, customColors) : null),
    [data, chartType, primaryColor, customColors],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 180 }}>
        <LoadingIndicator />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography color="error" variant="body2">
          Preview failed. Check your configuration.
        </Typography>
      </Box>
    );
  }

  // KPI rendering
  if (chartType === "kpi") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 180 }}>
        <Typography variant="h6" color="primary">{title}</Typography>
        <Typography variant="h3" color="secondary" sx={{ mt: 1 }}>
          {numberWithCommas(data?.total)}
        </Typography>
      </Box>
    );
  }

  if (!chartConfig) {
    return (
      <Box sx={{ minHeight: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h6" color="primary">{title}</Typography>
        <Typography color="text.secondary" sx={{ mt: 2 }}>No data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 180 }}>
      <Typography variant="h6" color="primary" sx={{ mb: 1 }}>{title}</Typography>
      {chartReady && (
        <Chart
          key={chartIdentity}
          options={chartConfig.options}
          series={chartConfig.series}
          type={chartConfig.type}
          height={160}
          width="100%"
        />
      )}
    </Box>
  );
}
